import { useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { regionalOrderApi, inventoryApi } from '../../services/api'
import Button from '../../components/common/Button'
import Input from '../../components/common/Input'

/**
 * Reorder — the regional reorder workflow.
 *
 * Three tabs:
 *   1. Build (default) — items below par with editable quantities,
 *      plus a way to add extras that aren't auto-flagged. Send to regional.
 *   2. History         — list of past regional orders, click to see line items.
 *
 * The "Send" button writes a regional_orders row + regional_order_items
 * rows and emails the regional supplier with the line items table.
 */
export default function Reorder() {
    const [tab, setTab] = useState('build')

    return (
        <div className="space-y-4">
            <div>
                <h2 className="text-2xl font-semibold text-ghasc-navy">Regional Reorder</h2>
                <p className="text-sm text-gray-500 mt-1">
                    Build a reorder list from items below par stock, then send it to the
                    regional supplier. Past reorders are kept for reference.
                </p>
            </div>

            {/* Tabs */}
            <div className="bg-white rounded-xl shadow-sm">
                <div className="border-b border-gray-200 flex">
                    <TabButton active={tab === 'build'} onClick={() => setTab('build')}>
                        Build &amp; Send
                    </TabButton>
                    <TabButton active={tab === 'history'} onClick={() => setTab('history')}>
                        History
                    </TabButton>
                </div>

                <div className="p-6">
                    {tab === 'build' ? <BuildTab /> : null}
                    {tab === 'history' ? <HistoryTab /> : null}
                </div>
            </div>
        </div>
    )
}

function TabButton({ active, onClick, children }) {
    return (
        <button
            onClick={onClick}
            className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${active
                    ? 'border-ghasc-navy text-ghasc-navy'
                    : 'border-transparent text-gray-500 hover:text-ghasc-navy'
                }`}
        >
            {children}
        </button>
    )
}

// ===========================================================================
//  BUILD TAB
// ===========================================================================
function BuildTab() {
    const [loading, setLoading] = useState(true)
    const [submitting, setSubmitting] = useState(false)
    const [notes, setNotes] = useState('')

    // Lines being built: keyed by inventory id
    //   { include: bool, quantity: int, item: InventoryItem }
    const [lines, setLines] = useState({})

    // Add-extras state
    const [showPicker, setShowPicker] = useState(false)
    const [pickerSearch, setPickerSearch] = useState('')
    const [allItems, setAllItems] = useState([])
    const [allItemsLoaded, setAllItemsLoaded] = useState(false)

    useEffect(() => { loadPreview() }, [])

    async function loadPreview() {
        setLoading(true)
        try {
            const response = await regionalOrderApi.preview()
            if (response.success) {
                // Convert suggestions to per-id lines, all included by default
                const next = {}
                for (const s of response.suggestions) {
                    next[s.item.id] = {
                        item: s.item,
                        include: true,
                        quantity: s.suggestedQuantity,
                    }
                }
                setLines(next)
            } else {
                toast.error(response.message || 'Could not load suggestions')
            }
        } catch {
            toast.error('Network error')
        } finally {
            setLoading(false)
        }
    }

    async function loadAllItems() {
        if (allItemsLoaded) return
        try {
            const response = await inventoryApi.list({})
            if (response.success) {
                setAllItems(response.items)
                setAllItemsLoaded(true)
            }
        } catch { /* non-fatal */ }
    }

    function updateLine(id, patch) {
        setLines(prev => ({
            ...prev,
            [id]: { ...prev[id], ...patch }
        }))
    }

    function removeLine(id) {
        setLines(prev => {
            const next = { ...prev }
            delete next[id]
            return next
        })
    }

    function addItem(item) {
        if (lines[item.id]) {
            toast('That item is already on the list')
            return
        }
        updateLine(item.id, {
            item,
            include: true,
            quantity: Math.max(1, (item.stockLimit || 1) - (item.quantity || 0)),
        })
        setShowPicker(false)
        setPickerSearch('')
    }

    async function handleSend() {
        const toSend = Object.values(lines)
            .filter(l => l.include && l.quantity > 0)
            .map(l => ({ inventoryId: l.item.id, quantity: l.quantity }))

        if (toSend.length === 0) {
            toast.error('Add at least one line and make sure it has a quantity')
            return
        }

        if (!confirm(`Send a reorder for ${toSend.length} line${toSend.length === 1 ? '' : 's'} to the regional supplier? This will email them and save the order to history.`)) {
            return
        }

        setSubmitting(true)
        try {
            const response = await regionalOrderApi.submit({
                notes: notes.trim() || null,
                items: toSend,
            })
            if (response.success) {
                toast.success(`Order sent`)
                setLines({})
                setNotes('')
                loadPreview()
            } else {
                toast.error(response.message || 'Could not send reorder')
            }
        } catch {
            toast.error('Network error')
        } finally {
            setSubmitting(false)
        }
    }

    // Computed totals
    const totals = useMemo(() => {
        let lineCount = 0
        let subtotal = 0
        for (const l of Object.values(lines)) {
            if (!l.include || !l.quantity) continue
            lineCount++
            subtotal += Number(l.item.cost) * Number(l.quantity)
        }
        return { lineCount, subtotal }
    }, [lines])

    if (loading) {
        return <div className="text-center text-gray-500 py-8">Loading suggestions...</div>
    }

    const linesArray = Object.values(lines)

    return (
        <div className="space-y-4">
            {linesArray.length === 0 ? (
                <div className="bg-gray-50 rounded-lg p-6 text-center text-gray-600">
                    No items currently below par stock. You can still add items manually below.
                </div>
            ) : (
                <ReorderTable
                    lines={linesArray}
                    onUpdate={updateLine}
                    onRemove={removeLine}
                />
            )}

            {/* Add extras */}
            <div className="border-t border-gray-100 pt-4">
                {!showPicker ? (
                    <button
                        type="button"
                        onClick={() => { setShowPicker(true); loadAllItems() }}
                        className="text-sm text-ghasc-navy hover:underline"
                    >
                        + Add another item not on the list
                    </button>
                ) : (
                    <ItemPicker
                        allItems={allItems}
                        loaded={allItemsLoaded}
                        existingIds={new Set(Object.keys(lines).map(Number))}
                        search={pickerSearch}
                        onSearchChange={setPickerSearch}
                        onPick={addItem}
                        onCancel={() => { setShowPicker(false); setPickerSearch('') }}
                    />
                )}
            </div>

            {/* Notes */}
            <div className="border-t border-gray-100 pt-4">
                <label htmlFor="ro-notes" className="block text-sm font-medium text-gray-700 mb-1">
                    Notes (optional, included in the email)
                </label>
                <textarea
                    id="ro-notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={3}
                    placeholder="Special instructions, requested delivery date, etc."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-ghasc-navy focus:border-transparent"
                />
            </div>

            {/* Totals + Send */}
            <div className="border-t border-gray-100 pt-4 flex items-center justify-between flex-wrap gap-3">
                <div className="text-sm text-gray-600">
                    {totals.lineCount} line{totals.lineCount === 1 ? '' : 's'} —{' '}
                    <span className="font-semibold text-gray-900">${totals.subtotal.toFixed(2)}</span>
                </div>
                <Button onClick={handleSend} loading={submitting} disabled={totals.lineCount === 0}>
                    Send to Regional
                </Button>
            </div>
        </div>
    )
}

// ---------------------------------------------------------------------------
//  Lines table (used in Build)
// ---------------------------------------------------------------------------
function ReorderTable({ lines, onUpdate, onRemove }) {
    return (
        <div className="overflow-x-auto">
            <table className="w-full">
                <thead className="bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <tr>
                        <th className="px-3 py-2 w-10 text-center">
                            <span className="sr-only">Include</span>
                        </th>
                        <th className="px-3 py-2">Item</th>
                        <th className="px-3 py-2 w-24 text-right">Stock</th>
                        <th className="px-3 py-2 w-24 text-right">Par</th>
                        <th className="px-3 py-2 w-28 text-right">Order Qty</th>
                        <th className="px-3 py-2 w-28 text-right">Line Total</th>
                        <th className="px-3 py-2 w-10"></th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                    {lines.map(({ item, include, quantity }) => {
                        const lineTotal = include ? Number(item.cost) * Number(quantity || 0) : 0
                        const variantParts = [item.year, item.medallionType, item.color].filter(Boolean)

                        return (
                            <tr key={item.id} className={include ? '' : 'opacity-50'}>
                                <td className="px-3 py-2 text-center">
                                    <input
                                        type="checkbox"
                                        checked={include}
                                        onChange={(e) => onUpdate(item.id, { include: e.target.checked })}
                                    />
                                </td>
                                <td className="px-3 py-2">
                                    <div className="text-sm font-medium text-gray-900">{item.description}</div>
                                    <div className="text-xs text-gray-500 font-mono">
                                        {item.itemNumber}
                                        {variantParts.length > 0 && (
                                            <span className="ml-2 font-sans">— {variantParts.join(' / ')}</span>
                                        )}
                                    </div>
                                    <div className="text-xs text-gray-500 mt-0.5">
                                        ${Number(item.cost).toFixed(2)} each
                                    </div>
                                </td>
                                <td className="px-3 py-2 text-right text-sm">{item.quantity}</td>
                                <td className="px-3 py-2 text-right text-sm">{item.stockLimit || '—'}</td>
                                <td className="px-3 py-2 text-right">
                                    <input
                                        type="number"
                                        min="1"
                                        value={quantity}
                                        onChange={(e) => {
                                            const n = parseInt(e.target.value, 10)
                                            onUpdate(item.id, { quantity: isNaN(n) ? 0 : n })
                                        }}
                                        disabled={!include}
                                        className="w-20 px-2 py-1 border border-gray-300 rounded text-right text-sm focus:outline-none focus:ring-2 focus:ring-ghasc-navy"
                                    />
                                </td>
                                <td className="px-3 py-2 text-right text-sm font-medium">
                                    ${lineTotal.toFixed(2)}
                                </td>
                                <td className="px-3 py-2 text-right">
                                    <button
                                        onClick={() => onRemove(item.id)}
                                        title="Remove this line"
                                        className="text-ghasc-danger text-sm hover:underline"
                                    >
                                        ✕
                                    </button>
                                </td>
                            </tr>
                        )
                    })}
                </tbody>
            </table>
        </div>
    )
}

// ---------------------------------------------------------------------------
//  Item picker — groups variants together (Medallions / Key Tags / Chips / etc.)
// ---------------------------------------------------------------------------
function ItemPicker({ allItems, loaded, existingIds, search, onSearchChange, onPick, onCancel }) {
    // expanded variant key currently being configured (null = none)
    const [expandedKey, setExpandedKey] = useState(null)
    // chosen variant id per group while configuring
    const [selectedVariant, setSelectedVariant] = useState({})

    // Build variant groups from the flat inventory list.
    // Items already on the reorder list are filtered out *per variant*, not per group,
    // so a group with one used variant + two free variants still shows.
    const groups = useMemo(() => {
        const map = new Map()
        for (const item of allItems) {
            const key = `${item.category}|${item.description}|${item.medallionType || ''}`
            if (!map.has(key)) {
                map.set(key, {
                    key,
                    category: item.category,
                    description: item.description,
                    medallionType: item.medallionType || null,
                    variants: [],
                })
            }
            map.get(key).variants.push(item)
        }

        // Drop variants already on the reorder list; drop groups that are now empty
        const result = []
        for (const g of map.values()) {
            const remaining = g.variants.filter(v => !existingIds.has(v.id))
            if (remaining.length > 0) {
                result.push({ ...g, variants: remaining })
            }
        }
        return result
    }, [allItems, existingIds])

    // Filter by search text against description, item_number, year, color
    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase()
        if (!q) return groups.slice(0, 20)
        return groups
            .filter(g => {
                if (g.description.toLowerCase().includes(q)) return true
                if ((g.medallionType || '').toLowerCase().includes(q)) return true
                if ((g.category || '').toLowerCase().includes(q)) return true
                return g.variants.some(v =>
                    v.itemNumber.toLowerCase().includes(q) ||
                    (v.year || '').toLowerCase().includes(q) ||
                    (v.color || '').toLowerCase().includes(q)
                )
            })
            .slice(0, 50)
    }, [groups, search])

    // Click a group: single-variant → add immediately, multi-variant → expand
    function handleGroupClick(group) {
        if (group.variants.length === 1) {
            onPick(group.variants[0])
            return
        }
        // Toggle expansion
        if (expandedKey === group.key) {
            setExpandedKey(null)
        } else {
            setExpandedKey(group.key)
            // Initialize selection to the first variant
            setSelectedVariant(s => ({ ...s, [group.key]: group.variants[0].id }))
        }
    }

    function handleVariantSelect(groupKey, inventoryId) {
        setSelectedVariant(s => ({ ...s, [groupKey]: inventoryId }))
    }

    function handleAddVariant(group) {
        const id = selectedVariant[group.key]
        const variant = group.variants.find(v => v.id === id)
        if (!variant) return
        onPick(variant)
        // Collapse so the next add starts fresh
        setExpandedKey(null)
    }

    return (
        <div className="bg-gray-50 rounded-lg p-3 space-y-2">
            <div className="flex items-end gap-2">
                <div className="flex-1">
                    <Input
                        id="picker-search"
                        label="Find an item to add"
                        placeholder="Item number, description, year, color..."
                        value={search}
                        onChange={(e) => onSearchChange(e.target.value)}
                        autoFocus
                    />
                </div>
                <button
                    onClick={onCancel}
                    className="text-sm text-gray-500 hover:underline pb-2"
                >
                    Cancel
                </button>
            </div>

            {!loaded ? (
                <div className="text-sm text-gray-500 py-2">Loading catalog...</div>
            ) : filtered.length === 0 ? (
                <div className="text-sm text-gray-500 py-2">No matching items.</div>
            ) : (
                <ul className="divide-y divide-gray-200 max-h-96 overflow-y-auto bg-white rounded border border-gray-200">
                    {filtered.map(group => (
                        <li key={group.key}>
                            <PickerRow
                                group={group}
                                expanded={expandedKey === group.key}
                                selectedVariantId={selectedVariant[group.key]}
                                onClick={() => handleGroupClick(group)}
                                onVariantSelect={(id) => handleVariantSelect(group.key, id)}
                                onAdd={() => handleAddVariant(group)}
                            />
                        </li>
                    ))}
                </ul>
            )}
        </div>
    )
}

// ---------------------------------------------------------------------------
//  One row in the picker. Renders differently for single vs multi-variant groups.
// ---------------------------------------------------------------------------
function PickerRow({ group, expanded, selectedVariantId, onClick, onVariantSelect, onAdd }) {
    const isMulti = group.variants.length > 1
    const isTriplate = group.medallionType === 'Triplate'

    // For multi-variant groups, the "summary" line shows count of variants;
    // for single, it shows the specific item details
    if (!isMulti) {
        const v = group.variants[0]
        const parts = [v.year, v.color].filter(Boolean)
        return (
            <button
                type="button"
                onClick={onClick}
                className="w-full px-3 py-2 hover:bg-gray-50 text-left"
            >
                <div className="text-sm text-gray-900">{v.description}</div>
                <div className="text-xs text-gray-500 font-mono">
                    {v.itemNumber}
                    {parts.length > 0 && (
                        <span className="ml-2 font-sans">— {parts.join(' / ')}</span>
                    )}
                    <span className="ml-2 font-sans">·{' '}
                        {v.quantity} in stock
                        {v.stockLimit ? `, par ${v.stockLimit}` : ''}
                    </span>
                </div>
            </button>
        )
    }

    // Multi-variant: derive distinct years and colors
    const years = [...new Set(group.variants.map(v => v.year).filter(Boolean))]
        .sort(naturalYearSort)
    const colors = isTriplate
        ? [...new Set(group.variants.map(v => v.color).filter(Boolean))].sort()
        : []

    const selected = group.variants.find(v => v.id === selectedVariantId) || group.variants[0]
    const selectedYear = selected?.year || ''
    const selectedColor = selected?.color || ''

    function pickYear(year) {
        const match = group.variants.find(v =>
            v.year === year && (isTriplate ? v.color === selectedColor : true)
        ) || group.variants.find(v => v.year === year)
        if (match) onVariantSelect(match.id)
    }

    function pickColor(color) {
        const match = group.variants.find(v =>
            v.color === color && v.year === selectedYear
        ) || group.variants.find(v => v.color === color)
        if (match) onVariantSelect(match.id)
    }

    return (
        <div>
            {/* Clickable summary row */}
            <button
                type="button"
                onClick={onClick}
                className="w-full px-3 py-2 hover:bg-gray-50 text-left"
            >
                <div className="text-sm text-gray-900 flex items-center justify-between">
                    <span>
                        {group.medallionType
                            ? <>{group.medallionType} {group.description.includes(group.medallionType) ? '' : group.description}</>
                            : group.description}
                    </span>
                    <span className="text-xs text-gray-400">{expanded ? '▾' : '▸'}</span>
                </div>
                <div className="text-xs text-gray-500">
                    {group.variants.length} variant{group.variants.length === 1 ? '' : 's'}
                    <span className="ml-1">— pick a year{isTriplate ? ' and color' : ''}</span>
                </div>
            </button>

            {/* Expanded variant configurator */}
            {expanded && (
                <div className="px-3 pb-3 pt-1 bg-gray-50 border-t border-gray-200">
                    <div className="grid sm:grid-cols-2 gap-2 mb-2">
                        <div>
                            <label className="block text-xs text-gray-600 mb-0.5">Year</label>
                            <select
                                value={selectedYear}
                                onChange={(e) => pickYear(e.target.value)}
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-ghasc-navy"
                            >
                                {years.map(y => <option key={y} value={y}>{y}</option>)}
                            </select>
                        </div>
                        {isTriplate && colors.length > 0 && (
                            <div>
                                <label className="block text-xs text-gray-600 mb-0.5">Color</label>
                                <select
                                    value={selectedColor}
                                    onChange={(e) => pickColor(e.target.value)}
                                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-ghasc-navy"
                                >
                                    {colors.map(c => <option key={c} value={c}>{c}</option>)}
                                </select>
                            </div>
                        )}
                    </div>

                    {/* Selected variant info + Add button */}
                    <div className="flex items-center justify-between text-xs text-gray-600 mb-2">
                        <span className="font-mono">
                            {selected?.itemNumber}
                            {' · '}
                            {selected?.quantity || 0} in stock
                            {selected?.stockLimit ? `, par ${selected.stockLimit}` : ''}
                            {' · '}
                            ${Number(selected?.cost || 0).toFixed(2)}
                        </span>
                        <button
                            type="button"
                            onClick={onAdd}
                            className="px-3 py-1 bg-ghasc-navy text-white rounded text-sm font-medium hover:opacity-90"
                        >
                            Add
                        </button>
                    </div>
                </div>
            )}
        </div>
    )
}

// ---------------------------------------------------------------------------
//  Sort year labels naturally: "6 Month" < "1 Year" < "10 Year"
// ---------------------------------------------------------------------------
function naturalYearSort(a, b) {
    const parseLabel = (s) => {
        const m = /^(\d+)\s*(month|year)/i.exec(s)
        if (!m) return [99999, s]
        const num = parseInt(m[1], 10)
        const unit = m[2].toLowerCase()
        return [unit === 'month' ? num : 1000 + num, s]
    }
    const [an, as] = parseLabel(a)
    const [bn, bs] = parseLabel(b)
    if (an !== bn) return an - bn
    return as.localeCompare(bs)
}

// ===========================================================================
//  HISTORY TAB
// ===========================================================================
function HistoryTab() {
    const [orders, setOrders] = useState([])
    const [loading, setLoading] = useState(true)
    const [expandedId, setExpandedId] = useState(null)
    const [details, setDetails] = useState({})        // id → full order with items

    useEffect(() => { load() }, [])

    async function load() {
        setLoading(true)
        try {
            const response = await regionalOrderApi.list()
            if (response.success) setOrders(response.orders)
            else toast.error(response.message || 'Could not load history')
        } catch {
            toast.error('Network error')
        } finally {
            setLoading(false)
        }
    }

    async function toggle(id) {
        if (expandedId === id) {
            setExpandedId(null)
            return
        }
        setExpandedId(id)
        if (!details[id]) {
            try {
                const response = await regionalOrderApi.getById(id)
                if (response.success) {
                    setDetails(d => ({ ...d, [id]: response.order }))
                }
            } catch {
                toast.error('Could not load order details')
            }
        }
    }

    async function handlePreview(id, e) {
        e.stopPropagation()   // don't trigger toggle
        try {
            const { blob } = await regionalOrderApi.fetchPdf(id, { inline: true })
            const url = URL.createObjectURL(blob)
            window.open(url, '_blank')
            setTimeout(() => URL.revokeObjectURL(url), 60_000)
        } catch {
            toast.error('Could not load PDF')
        }
    }

    async function handleDownload(id, e) {
        e.stopPropagation()
        try {
            const { blob, fileName } = await regionalOrderApi.fetchPdf(id, { inline: false })
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = fileName
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
            URL.revokeObjectURL(url)
        } catch {
            toast.error('Could not download PDF')
        }
    }

    if (loading) return <div className="text-center text-gray-500 py-8">Loading...</div>
    if (orders.length === 0) {
        return <div className="text-center text-gray-500 py-8">No reorders sent yet.</div>
    }

    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {orders.map(o => {
                const expanded = expandedId === o.id
                const detail = details[o.id]
                return (
                    <div key={o.id} className="bg-white rounded-xl shadow-sm p-4 flex flex-col gap-3">
                        {/* Top row: order id + date */}
                        <div className="flex items-start justify-between gap-2">
                            <div>
                                <div className="text-ghasc-navy font-semibold">Reorder #{o.id}</div>
                                <div className="text-xs text-gray-500 mt-0.5">
                                    {new Date(o.sentAt).toLocaleDateString('en-US', {
                                        month: 'short', day: 'numeric', year: 'numeric',
                                    })}
                                </div>
                            </div>
                            <div className="text-right">
                                <div className="text-xs uppercase tracking-wider text-gray-500">Total</div>
                                <div className="font-semibold text-ghasc-navy">
                                    ${Number(o.subtotal).toFixed(2)}
                                </div>
                            </div>
                        </div>

                        {/* Recipient + lines */}
                        <div>
                            <div className="text-sm font-medium text-gray-900 break-words">
                                → {o.sentTo}
                            </div>
                            <div className="text-xs text-gray-500 mt-0.5">
                                {o.lineCount} line{o.lineCount === 1 ? '' : 's'}
                                {o.sentByUserName ? ` · sent by ${o.sentByUserName}` : ''}
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100">
                            <button
                                type="button"
                                onClick={(e) => handlePreview(o.id, e)}
                                className="flex-1 min-w-[90px] px-3 py-2 text-sm text-ghasc-navy bg-gray-50 rounded-lg hover:bg-gray-100"
                            >
                                Preview
                            </button>
                            <button
                                type="button"
                                onClick={(e) => handleDownload(o.id, e)}
                                className="flex-1 min-w-[90px] px-3 py-2 text-sm text-ghasc-navy bg-gray-50 rounded-lg hover:bg-gray-100"
                            >
                                Download
                            </button>
                            <button
                                type="button"
                                onClick={() => toggle(o.id)}
                                className="flex-1 min-w-[90px] px-3 py-2 text-sm text-gray-700 bg-gray-50 rounded-lg hover:bg-gray-100"
                            >
                                {expanded ? 'Hide items' : 'Show items'}
                            </button>
                        </div>

                        {/* Expanded items */}
                        {expanded && (
                            <div className="border-t border-gray-100 pt-3 -mx-1">
                                {!detail ? (
                                    <div className="text-sm text-gray-500 text-center py-2">Loading details...</div>
                                ) : (
                                    <HistoryDetail order={detail} />
                                )}
                            </div>
                        )}
                    </div>
                )
            })}
        </div>
    )
}

function HistoryDetail({ order }) {
    return (
        <div className="space-y-3">
            {order.notes && (
                <div className="text-sm">
                    <span className="text-gray-500">Notes:</span>{' '}
                    <span className="text-gray-700 whitespace-pre-line">{order.notes}</span>
                </div>
            )}
            <div className="overflow-x-auto bg-white rounded border border-gray-200">
                <table className="w-full text-sm">
                    <thead className="text-xs uppercase tracking-wider text-gray-500 bg-gray-50">
                        <tr>
                            <th className="px-3 py-2 text-left">Item</th>
                            <th className="px-3 py-2 text-right w-20">Qty</th>
                            <th className="px-3 py-2 text-right w-24">Unit</th>
                            <th className="px-3 py-2 text-right w-24">Line</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                        {order.items.map(item => {
                            const lineTotal = Number(item.cost) * Number(item.quantity)
                            const variantParts = [
                                item.year,
                                item.medallionType,
                                // Show order_code for Triplate (it's what regional uses); fall back to color name
                                item.medallionType === 'Triplate' && item.colorOrderCode
                                    ? item.colorOrderCode
                                    : item.color,
                            ].filter(Boolean)
                            return (
                                <tr key={item.id}>
                                    <td className="px-3 py-2">
                                        <div className="text-gray-900">{item.description}</div>
                                        <div className="text-xs text-gray-500 font-mono">
                                            {item.itemNumber}
                                            {variantParts.length > 0 && (
                                                <span className="ml-2 font-sans">— {variantParts.join(' / ')}</span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-3 py-2 text-right">{item.quantity}</td>
                                    <td className="px-3 py-2 text-right">${Number(item.cost).toFixed(2)}</td>
                                    <td className="px-3 py-2 text-right">${lineTotal.toFixed(2)}</td>
                                </tr>
                            )
                        })}
                        <tr>
                            <td colSpan={3} className="px-3 py-2 text-right font-medium text-gray-700">
                                Subtotal
                            </td>
                            <td className="px-3 py-2 text-right font-semibold text-gray-900">
                                ${Number(order.subtotal).toFixed(2)}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    )
}